import { callText } from "@/lib/ai/karma-core/providerClient";
import { VoiceTranscriptBuffer } from "./voiceTranscriptBuffer";

export interface IntentJudgment {
  directed: boolean;
  query: string;
  stop: boolean;
  confidence: "high" | "medium" | "low";
  reasoning: string;
}

function extractJsonObject(text: string): string {
  const candidates: string[] = [];
  let searchFrom = 0;
  while (true) {
    const start = text.indexOf("{", searchFrom);
    if (start === -1) break;
    let depth = 0;
    let inString = false;
    let escape = false;
    let end = -1;
    for (let i = start; i < text.length; i++) {
      const ch = text[i];
      if (inString) {
        if (escape) {
          escape = false;
        } else if (ch === "\\") {
          escape = true;
        } else if (ch === '"') {
          inString = false;
        }
        continue;
      }
      if (ch === '"') {
        inString = true;
      } else if (ch === "{") {
        depth += 1;
      } else if (ch === "}") {
        depth -= 1;
        if (depth === 0) {
          end = i + 1;
          break;
        }
      }
    }
    if (end === -1) {
      searchFrom = start + 1;
      continue;
    }
    candidates.push(text.slice(start, end));
    searchFrom = end;
  }
  return candidates.length > 0 ? candidates[candidates.length - 1] : "";
}

export async function judgeVoiceIntent(
  assistantName: string,
  rawTranscript: string,
  buffer: VoiceTranscriptBuffer,
  isHotWindow: boolean
): Promise<IntentJudgment> {
  const recentTranscripts = buffer.getLastSeconds(120);
  const previousTranscripts = recentTranscripts
    .filter((s) => s.text !== rawTranscript)
    .map((s) => `[${s.isDuringTts ? "TTS" : "user"}] ${s.text}`)
    .join("\n");

  const systemPrompt = `You are the intent judge for voice assistant "${assistantName}".

Two modes:

WAKE WORD / DIRECT ADDRESS MODE:
- Extract the complete query directed at "${assistantName}".
- Remove every occurrence of "${assistantName}" from the query unless the user is literally talking ABOUT the assistant as a subject.
- If the current query contains vague references ("that", "it", "this", "they") and earlier transcripts establish the topic, resolve the pronoun to the actual entity name.

HOT WINDOW MODE (no wake word needed):
- User IS DIRECTED — always, no exceptions, regardless of transcript content.
- Extract the user's complete request from the current transcript literally.
- If the transcript is just a statement, treat it as a request for a response.

STOP DETECTION:
- "stop", "quiet", "shut up", "never mind" (standalone) → directed=true, stop=true, query=""

NOT DIRECTED:
- No wake word and not hot window → directed=false.
- Transcript marked [TTS] is echo of assistant → directed=false.

Output JSON only:
{"directed": true/false, "query": "...", "stop": true/false, "confidence": "high/medium/low", "reasoning": "brief"}

Examples:
- "${assistantName} what time is it" → {"directed": true, "query": "what time is it", "stop": false, "confidence": "high", "reasoning": "wake word + question"}
- Previous "dinosaurs are cool" + Current "${assistantName} what do you think about that" → {"directed": true, "query": "what do you think about dinosaurs being cool", "stop": false, "confidence": "high", "reasoning": "resolved 'that' to dinosaurs"}
- Hot window, user says "I think absurdism is better" → {"directed": true, "query": "I think absurdism is better", "stop": false, "confidence": "high", "reasoning": "user statement in hot window"}
- "${assistantName} stop" → {"directed": true, "query": "", "stop": true, "confidence": "high", "reasoning": "stop command"}`;

  const userContent = `Recent transcripts:\n${previousTranscripts || "(none)"}\n\nCurrent transcript to judge: "${rawTranscript}"\n\nHot window: ${isHotWindow ? "yes" : "no"}`;

  try {
    const result = await callText({
      messages: [
        { role: "system", content: systemPrompt },
        { role: "user", content: userContent },
      ],
      strategy: "FAST",
      maxTokens: 512,
    });

    const cleaned = extractJsonObject(result.text);
    if (!cleaned) {
      // A failed/unparseable judge call carries no real signal either way —
      // defaulting to isHotWindow meant a provider hiccup during a stale
      // hot window let ambient noise straight through as if directed.
      // Fail closed: worst case is asking the user to repeat themselves,
      // not Karma responding out loud to background conversation.
      return {
        directed: false,
        query: rawTranscript,
        stop: false,
        confidence: "low",
        reasoning: "Failed to parse JSON verdict; failing closed rather than assuming directed.",
      };
    }

    const parsed = JSON.parse(cleaned) as Partial<IntentJudgment>;
    return {
      directed: parsed.directed ?? false,
      query: parsed.query?.trim() || rawTranscript,
      stop: parsed.stop ?? false,
      confidence: (parsed.confidence as any) || "medium",
      reasoning: parsed.reasoning || "",
    };
  } catch (error: any) {
    return {
      directed: false,
      query: rawTranscript,
      stop: false,
      confidence: "low",
      reasoning: `Intent judge error: ${error.message || String(error)} — failing closed.`,
    };
  }
}
